export const USER_ROLES = ['ADMIN', 'MANAGER', 'REVIEWER', 'OPERATOR', 'VIEWER'] as const;
export type UserRole = (typeof USER_ROLES)[number];

export const PLAN_TYPES = ['STARTER', 'PROFESSIONAL', 'ENTERPRISE'] as const;
export type PlanType = (typeof PLAN_TYPES)[number];

export const DOCUMENT_STATUSES = [
  'UPLOADED',
  'PROCESSING',
  'TEXT_EXTRACTED',
  'PROCESSED',
  'FAILED',
] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

// Industry-comprehensive document types used across pharma regulatory,
// clinical, CMC, pharmacovigilance, and quality/GxP workflows.
// Organised below for readability -- runtime order is stable (add to end, never reorder).
export const DOCUMENT_TYPES = [
  // --- Clinical ---------------------------------------------
  'CLINICAL_STUDY_REPORT',
  'CLINICAL_STUDY_PROTOCOL',
  'PROTOCOL_AMENDMENT',
  'INVESTIGATOR_BROCHURE',
  'INFORMED_CONSENT',
  'CASE_REPORT_FORM',
  'STATISTICAL_ANALYSIS_PLAN',
  'CLINICAL_OVERVIEW',
  'CLINICAL_SUMMARY',
  'PATIENT_NARRATIVE',
  'SUBJECT_DIARY',
  'MONITORING_VISIT_REPORT',
  'TMF_DOCUMENT',
  // --- Regulatory submissions (eCTD) ------------------------
  'IND_APPLICATION',
  'NDA_BLA_APPLICATION',
  'MAA_APPLICATION',
  'CTA_APPLICATION',
  'ECTD_MODULE',
  'ORPHAN_DRUG_DESIGNATION',
  'PAEDIATRIC_INVESTIGATION_PLAN',
  'HA_MEETING_PACKAGE',
  'HA_CORRESPONDENCE',
  'HA_RESPONSE',
  'VARIATION_SUPPLEMENT',
  'SMPC',                          // Summary of Product Characteristics
  'PATIENT_INFORMATION_LEAFLET',
  'RISK_MANAGEMENT_PLAN',
  // --- CMC (Chemistry, Manufacturing, Controls) -------------
  'CMC_SECTION',
  'DRUG_SUBSTANCE_SPEC',
  'DRUG_PRODUCT_SPEC',
  'ANALYTICAL_METHOD_VALIDATION',
  'STABILITY_STUDY_REPORT',
  'PROCESS_VALIDATION',
  'QUALITY_RISK_ASSESSMENT',
  'MANUFACTURING_BATCH_RECORD',
  'CERTIFICATE_OF_ANALYSIS',
  // --- Pharmacovigilance ------------------------------------
  'ADVERSE_EVENT_REPORT',
  'ICSR',                          // Individual Case Safety Report
  'E2B_XML',
  'PSUR',                          // Periodic Safety Update Report
  'PBRER',                         // Periodic Benefit-Risk Evaluation Report
  'DSUR',                          // Development Safety Update Report
  'SAFETY_NARRATIVE',
  'PSMF',                          // Pharmacovigilance System Master File
  'SDEA',                          // Safety Data Exchange Agreement
  'REFERENCE_SAFETY_INFORMATION',  // RSI / CCDS
  'SIGNAL_DETECTION_REPORT',
  'REMS',                          // Risk Evaluation and Mitigation Strategy
  // --- Quality / GxP / QMS ----------------------------------
  'SOP',                           // Standard Operating Procedure
  'WORK_INSTRUCTION',
  'QUALITY_MANUAL',
  'VALIDATION_MASTER_PLAN',
  'DEVIATION_REPORT',
  'CAPA',                          // Corrective and Preventive Action
  'CHANGE_CONTROL',
  'AUDIT_REPORT',
  'INSPECTION_REPORT',
  'TRAINING_RECORD',
  // --- Regulatory intelligence / literature -----------------
  'LITERATURE_ARTICLE',
  'REGULATORY_GUIDELINE',
  'REGULATORY_SUBMISSION',         // generic container
  // --- Catch-all --------------------------------------------
  'OTHER',
] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

// Human-readable labels for the UI (keys match enum values).
export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  CLINICAL_STUDY_REPORT: 'Clinical Study Report (CSR)',
  CLINICAL_STUDY_PROTOCOL: 'Clinical Study Protocol',
  PROTOCOL_AMENDMENT: 'Protocol Amendment',
  INVESTIGATOR_BROCHURE: 'Investigator Brochure (IB)',
  INFORMED_CONSENT: 'Informed Consent Form',
  CASE_REPORT_FORM: 'Case Report Form (CRF)',
  STATISTICAL_ANALYSIS_PLAN: 'Statistical Analysis Plan',
  CLINICAL_OVERVIEW: 'Clinical Overview',
  CLINICAL_SUMMARY: 'Clinical Summary',
  PATIENT_NARRATIVE: 'Patient Narrative',
  SUBJECT_DIARY: 'Subject Diary',
  MONITORING_VISIT_REPORT: 'Monitoring Visit Report',
  TMF_DOCUMENT: 'Trial Master File Document',
  IND_APPLICATION: 'IND Application',
  NDA_BLA_APPLICATION: 'NDA / BLA Application',
  MAA_APPLICATION: 'Marketing Authorisation Application',
  CTA_APPLICATION: 'Clinical Trial Application',
  ECTD_MODULE: 'eCTD Module',
  ORPHAN_DRUG_DESIGNATION: 'Orphan Drug Designation',
  PAEDIATRIC_INVESTIGATION_PLAN: 'Paediatric Investigation Plan (PIP)',
  HA_MEETING_PACKAGE: 'Health Authority Meeting Package',
  HA_CORRESPONDENCE: 'Health Authority Correspondence',
  HA_RESPONSE: 'Response to Health Authority',
  VARIATION_SUPPLEMENT: 'Variation / Supplement',
  SMPC: 'Summary of Product Characteristics (SmPC)',
  PATIENT_INFORMATION_LEAFLET: 'Patient Information Leaflet (PIL)',
  RISK_MANAGEMENT_PLAN: 'Risk Management Plan (RMP)',
  CMC_SECTION: 'CMC Section (Module 3)',
  DRUG_SUBSTANCE_SPEC: 'Drug Substance Specification',
  DRUG_PRODUCT_SPEC: 'Drug Product Specification',
  ANALYTICAL_METHOD_VALIDATION: 'Analytical Method Validation',
  STABILITY_STUDY_REPORT: 'Stability Study Report',
  PROCESS_VALIDATION: 'Process Validation',
  QUALITY_RISK_ASSESSMENT: 'Quality Risk Assessment',
  MANUFACTURING_BATCH_RECORD: 'Manufacturing Batch Record',
  CERTIFICATE_OF_ANALYSIS: 'Certificate of Analysis (CoA)',
  ADVERSE_EVENT_REPORT: 'Adverse Event Report',
  ICSR: 'Individual Case Safety Report (ICSR)',
  E2B_XML: 'E2B(R3) XML',
  PSUR: 'Periodic Safety Update Report (PSUR)',
  PBRER: 'Periodic Benefit-Risk Evaluation Report (PBRER)',
  DSUR: 'Development Safety Update Report (DSUR)',
  SAFETY_NARRATIVE: 'Safety Narrative',
  PSMF: 'Pharmacovigilance System Master File (PSMF)',
  SDEA: 'Safety Data Exchange Agreement (SDEA)',
  REFERENCE_SAFETY_INFORMATION: 'Reference Safety Information / CCDS',
  SIGNAL_DETECTION_REPORT: 'Signal Detection Report',
  REMS: 'Risk Evaluation and Mitigation Strategy (REMS)',
  SOP: 'Standard Operating Procedure (SOP)',
  WORK_INSTRUCTION: 'Work Instruction',
  QUALITY_MANUAL: 'Quality Manual',
  VALIDATION_MASTER_PLAN: 'Validation Master Plan',
  DEVIATION_REPORT: 'Deviation Report',
  CAPA: 'Corrective and Preventive Action (CAPA)',
  CHANGE_CONTROL: 'Change Control Record',
  AUDIT_REPORT: 'Audit Report',
  INSPECTION_REPORT: 'Inspection Report',
  TRAINING_RECORD: 'Training Record',
  LITERATURE_ARTICLE: 'Literature Article',
  REGULATORY_GUIDELINE: 'Regulatory Guideline',
  REGULATORY_SUBMISSION: 'Regulatory Submission',
  OTHER: 'Other',
};

// Thematic groups for UI grouping/filtering.
export const DOCUMENT_TYPE_CATEGORIES = {
  CLINICAL: [
    'CLINICAL_STUDY_REPORT',
    'CLINICAL_STUDY_PROTOCOL',
    'PROTOCOL_AMENDMENT',
    'INVESTIGATOR_BROCHURE',
    'INFORMED_CONSENT',
    'CASE_REPORT_FORM',
    'STATISTICAL_ANALYSIS_PLAN',
    'CLINICAL_OVERVIEW',
    'CLINICAL_SUMMARY',
    'PATIENT_NARRATIVE',
    'SUBJECT_DIARY',
    'MONITORING_VISIT_REPORT',
    'TMF_DOCUMENT',
  ],
  REGULATORY: [
    'IND_APPLICATION',
    'NDA_BLA_APPLICATION',
    'MAA_APPLICATION',
    'CTA_APPLICATION',
    'ECTD_MODULE',
    'ORPHAN_DRUG_DESIGNATION',
    'PAEDIATRIC_INVESTIGATION_PLAN',
    'HA_MEETING_PACKAGE',
    'HA_CORRESPONDENCE',
    'HA_RESPONSE',
    'VARIATION_SUPPLEMENT',
    'SMPC',
    'PATIENT_INFORMATION_LEAFLET',
    'RISK_MANAGEMENT_PLAN',
    'REGULATORY_SUBMISSION',
    'REGULATORY_GUIDELINE',
  ],
  CMC: [
    'CMC_SECTION',
    'DRUG_SUBSTANCE_SPEC',
    'DRUG_PRODUCT_SPEC',
    'ANALYTICAL_METHOD_VALIDATION',
    'STABILITY_STUDY_REPORT',
    'PROCESS_VALIDATION',
    'QUALITY_RISK_ASSESSMENT',
    'MANUFACTURING_BATCH_RECORD',
    'CERTIFICATE_OF_ANALYSIS',
  ],
  PHARMACOVIGILANCE: [
    'ADVERSE_EVENT_REPORT',
    'ICSR',
    'E2B_XML',
    'PSUR',
    'PBRER',
    'DSUR',
    'SAFETY_NARRATIVE',
    'PSMF',
    'SDEA',
    'REFERENCE_SAFETY_INFORMATION',
    'SIGNAL_DETECTION_REPORT',
    'REMS',
    'LITERATURE_ARTICLE',
  ],
  QUALITY: [
    'SOP',
    'WORK_INSTRUCTION',
    'QUALITY_MANUAL',
    'VALIDATION_MASTER_PLAN',
    'DEVIATION_REPORT',
    'CAPA',
    'CHANGE_CONTROL',
    'AUDIT_REPORT',
    'INSPECTION_REPORT',
    'TRAINING_RECORD',
  ],
  OTHER: ['OTHER'],
} as const satisfies Record<string, readonly DocumentType[]>;

export const WORKFLOW_TYPES = [
  'REGULATORY_DOC_INTELLIGENCE',
  'PHARMACOVIGILANCE',
  'CLINICAL_TRIAL_DOCS',
  'REAL_WORLD_EVIDENCE',
  'CUSTOM',
] as const;
export type WorkflowType = (typeof WORKFLOW_TYPES)[number];

export const WORKFLOW_RUN_STATUSES = [
  'QUEUED',
  'RUNNING',
  'AWAITING_REVIEW',
  'COMPLETED',
  'FAILED',
  'CANCELLED',
] as const;
export type WorkflowRunStatus = (typeof WORKFLOW_RUN_STATUSES)[number];

export const VALIDATION_STATUSES = [
  'PENDING',
  'PASSED',
  'FAILED',
  'PASSED_WITH_WARNINGS',
] as const;
export type ValidationStatus = (typeof VALIDATION_STATUSES)[number];

export const REVIEW_STATUSES = [
  'PENDING',
  'APPROVED',
  'REJECTED',
  'CORRECTIONS_APPLIED',
] as const;
export type ReviewStatus = (typeof REVIEW_STATUSES)[number];

export const AUDIT_ACTIONS = [
  // Document lifecycle
  'DOCUMENT_UPLOADED',
  'DOCUMENT_VIEWED',
  'DOCUMENT_PROCESSED',
  'DOCUMENT_DELETED',
  // Workflow lifecycle
  'WORKFLOW_CREATED',
  'WORKFLOW_STARTED',
  'WORKFLOW_STEP_COMPLETED',
  'WORKFLOW_APPROVED',
  'WORKFLOW_REJECTED',
  'WORKFLOW_COMPLETED',
  'WORKFLOW_FAILED',
  // AI actions
  'AI_EXTRACTION_STARTED',
  'AI_EXTRACTION_COMPLETED',
  'AI_EXTRACTION_FAILED',
  'AI_MODEL_CALLED',
  'AI_OUTPUT_GENERATED',
  'AI_OUTPUT_VALIDATED',
  'AI_OUTPUT_REVIEWED',
  // User actions
  'USER_LOGIN',
  'USER_LOGOUT',
  'USER_CREATED',
  'USER_UPDATED',
  'USER_DEACTIVATED',
  // E-signature
  'ESIGNATURE_APPLIED',
  'ESIGNATURE_VERIFIED',
  // System
  'SYSTEM_CONFIG_CHANGED',
  'SYSTEM_BACKUP_CREATED',
  'EXPORT_GENERATED',
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const MODEL_PROVIDERS = ['claude', 'gemini', 'openai'] as const;
export type ModelProvider = (typeof MODEL_PROVIDERS)[number];

export const TASK_KEYS = [
  'extraction',
  'classification',
  'coding',
  'summarisation',
  'cross_reference',
  'narrative_generation',
  'routing',
  'validation',
  'signal_detection',
  'rmp_authoring',
  'psur_authoring',
  'literature_screening',
  'duplicate_detection',
  'snapshot_generator',
] as const;
export type TaskKey = (typeof TASK_KEYS)[number];

// Risk tiers per EMA AI Reflection Paper Section 2.2.
// HIGH_PATIENT_RISK: errors can harm humans (icsr coding, seriousness,
//   signal detection, duplicate detection).
// HIGH_REGULATORY_IMPACT: errors affect regulatory submissions (psmf-scan,
//   psur, rmp, csr extraction, cmc analysis).
// STANDARD: lower-risk classification / analysis.
export const RISK_TIERS = ['HIGH_PATIENT_RISK', 'HIGH_REGULATORY_IMPACT', 'STANDARD'] as const;
export type RiskTier = (typeof RISK_TIERS)[number];

// Per-skill risk tier assignments. Drives:
// - mandatory human-in-the-loop (HIGH_PATIENT_RISK, HIGH_REGULATORY_IMPACT)
// - frozen model + prompt pinning (HIGH_REGULATORY_IMPACT)
// - drift monitoring cadence (all tiers; weekly for HIGH, monthly for STANDARD).
export const SKILL_RISK_TIERS: Record<string, RiskTier> = {
  // Pharmacovigilance operational (HIGH_PATIENT_RISK)
  'meddra-coding': 'HIGH_PATIENT_RISK',
  'safety-narrative': 'HIGH_PATIENT_RISK',
  'signal-detection': 'HIGH_PATIENT_RISK',
  'duplicate-detection': 'HIGH_PATIENT_RISK',
  // Regulatory documents (HIGH_REGULATORY_IMPACT)
  'psmf-scan': 'HIGH_REGULATORY_IMPACT',
  'sop-scan': 'HIGH_REGULATORY_IMPACT',
  'regulatory-csr-extraction': 'HIGH_REGULATORY_IMPACT',
  'cmc-analysis': 'HIGH_REGULATORY_IMPACT',
  'psur-writer': 'HIGH_REGULATORY_IMPACT',
  'rmp-assist': 'HIGH_REGULATORY_IMPACT',
  // Classification / analysis (STANDARD)
  'literature-monitor': 'STANDARD',
  'cross-reference-check': 'STANDARD',
  'gap-analysis': 'STANDARD',
};

// Default enforcement flags per tier.
export const RISK_TIER_DEFAULTS: Record<
  RiskTier,
  { humanReviewRequired: boolean; driftMonitoringEnabled: boolean; frozenModelRequired: boolean }
> = {
  HIGH_PATIENT_RISK: {
    humanReviewRequired: true,
    driftMonitoringEnabled: true,
    frozenModelRequired: true,
  },
  HIGH_REGULATORY_IMPACT: {
    humanReviewRequired: true,
    driftMonitoringEnabled: true,
    frozenModelRequired: true,
  },
  STANDARD: {
    humanReviewRequired: false,
    driftMonitoringEnabled: true,
    frozenModelRequired: false,
  },
};

// Role hierarchy -- higher index = more privileged.
// Used for simple >= checks in the frontend (server enforces via middleware).
export const ROLE_RANK: Record<UserRole, number> = {
  VIEWER: 0,
  OPERATOR: 1,
  REVIEWER: 2,
  MANAGER: 3,
  ADMIN: 4,
};

export const MAX_UPLOAD_SIZE_DEFAULT_MB = 100;
export const SUPPORTED_UPLOAD_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.spreadsheet',
  'text/plain',
] as const;

// --- Controlled vocabulary for e-signature reason codes (21 CFR Part 11) --
// Free-text reasons are allowed as supplementary comments but one of these
// codes must be selected. Codes align with common industry practice (Veeva,
// TrackWise) so auditors recognise them.
export const ESIG_REASON_CODES = {
  approve: [
    { code: 'APPROVED_AS_DRAFTED', label: 'Approved as drafted' },
    { code: 'APPROVED_AFTER_VERIFICATION', label: 'Approved after source verification' },
    { code: 'APPROVED_WITH_CONDITIONS', label: 'Approved with conditions (see notes)' },
    { code: 'APPROVED_FOR_RELEASE', label: 'Approved for release / submission' },
  ],
  reject: [
    { code: 'REJECTED_DATA_QUALITY', label: 'Rejected -- data quality issues' },
    { code: 'REJECTED_INCORRECT_SCOPE', label: 'Rejected -- incorrect scope / wrong document' },
    { code: 'REJECTED_INSUFFICIENT_EVIDENCE', label: 'Rejected -- insufficient supporting evidence' },
    { code: 'REJECTED_REQUIRES_RE_ANALYSIS', label: 'Rejected -- requires re-analysis' },
  ],
  correct: [
    { code: 'CORRECTED_TRANSCRIPTION', label: 'Corrected transcription / OCR errors' },
    { code: 'CORRECTED_NUMERIC', label: 'Corrected numeric values against source' },
    { code: 'CORRECTED_CLASSIFICATION', label: 'Corrected classification / categorisation' },
    { code: 'CORRECTED_CODING', label: 'Corrected MedDRA / dictionary coding' },
  ],
} as const;

export type EsigAction = keyof typeof ESIG_REASON_CODES;
